import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Image as ImageIcon, RefreshCw, CheckCircle2, XCircle, Clock, Eye, Search, Settings, PlusCircle } from 'lucide-react';

const STATUS_BADGE = {
    pending: <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>,
    active: <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Activo</Badge>,
    rejected: <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>,
    paused: <Badge variant="outline" className="text-muted-foreground">Pausado</Badge>,
    expired: <Badge variant="outline" className="text-muted-foreground">Expirado</Badge>,
};

const BANNER_PLACEMENTS = {
    home_top: 'Inicio — arriba del fold',
    home_mid: 'Inicio — sección media',
    category_top: 'Categorías — parte superior',
    search_top: 'Buscador — sobre resultados',
    sidebar: 'Barra lateral',
};

export default function AdminSellerBanners() {
    const [banners, setBanners] = useState([]);
    const [pricing, setPricing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [reviewOpen, setReviewOpen] = useState(false);
    const [pricingOpen, setPricingOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [pricingForm, setPricingForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data }, { data: pr }] = await Promise.all([
                api.get('/admin/seller-banners', { params: filterStatus !== '__all__' ? { status: filterStatus } : {} }),
                api.get('/admin/seller-banners/pricing'),
            ]);
            setBanners(Array.isArray(data) ? data : []);
            setPricing(pr);
        } catch (e) { toast.error('Error al cargar banners'); }
        finally { setLoading(false); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const openReview = (b) => { setSelected(b); setAdminNote(''); setReviewOpen(true); };

    const openPricing = () => {
        setPricingForm(JSON.parse(JSON.stringify(pricing)));
        setPricingOpen(true);
    };

    const savePricing = async () => {
        setBusy(true);
        try {
            await api.put('/admin/seller-banners/pricing', pricingForm);
            toast.success('Precios de banners actualizados');
            setPricingOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const doAction = async (action) => {
        setBusy(true);
        try {
            await api.put(`/admin/seller-banners/${selected.id}/review`, { action, admin_note: adminNote });
            toast.success(action === 'approve' ? 'Banner aprobado y activado' : action === 'pause' ? 'Banner pausado' : 'Banner rechazado');
            setReviewOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    const pendingCount = banners.filter(b => b.status === 'pending').length;
    const filtered = banners.filter(b => !search || b.seller_nickname?.toLowerCase().includes(search.toLowerCase()) || b.title?.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-5" data-testid="admin-seller-banners">
            <div className="flex items-center gap-3 flex-wrap">
                <ImageIcon className="h-6 w-6 text-blue-500" />
                <h1 className="text-2xl font-semibold">Banners de Vendedores</h1>
                {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-800">{pendingCount} pendientes</Badge>}
                <Button size="sm" variant="outline" onClick={load} className="ml-auto"><RefreshCw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            {/* Pricing summary */}
            {pricing && (
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="font-medium flex items-center gap-2"><Settings className="h-4 w-4 text-muted-foreground" />Configuración de precios de slots</div>
                        <Button size="sm" variant="outline" onClick={openPricing}>Editar precios</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {(pricing.packages || []).map((p, i) => (
                            <div key={i} className="text-sm rounded-lg border p-2.5 bg-secondary/30">
                                <div className="font-medium">{p.label}</div>
                                <div className="text-muted-foreground text-xs">{p.slots} slot{p.slots !== 1 ? 's' : ''} · {p.days} días</div>
                                <div className="text-muted-foreground text-xs">${(p.price_mxn || 0).toLocaleString('es-MX')} MXN</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="active">Activos</SelectItem>
                        <SelectItem value="rejected">Rechazados</SelectItem>
                        <SelectItem value="paused">Pausados</SelectItem>
                        <SelectItem value="expired">Expirados</SelectItem>
                        <SelectItem value="__all__">Todos</SelectItem>
                    </SelectContent>
                </Select>
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-8" placeholder="Buscar por vendedor…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed py-14 text-center text-sm text-muted-foreground">
                    No hay banners con este filtro.
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map(b => (
                        <div key={b.id} className="rounded-xl border bg-card overflow-hidden" data-testid={`admin-banner-${b.id}`}>
                            <div className="flex items-start gap-4 p-4">
                                {b.image_url && (
                                    <img
                                        src={b.image_url.startsWith('http') ? b.image_url : `/api/media/${b.image_url.replace(/^\/api\/media\//, '')}`}
                                        alt={b.title}
                                        className="h-20 w-40 object-cover rounded-lg shrink-0 border"
                                        onError={e => { e.target.style.display = 'none'; }}
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">{b.title}</span>
                                        {STATUS_BADGE[b.status] || <Badge variant="outline">{b.status}</Badge>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        @{b.seller_nickname} · {BANNER_PLACEMENTS[b.placement] || b.placement}
                                    </div>
                                    {b.link_url && (
                                        <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline truncate block">{b.link_url}</a>
                                    )}
                                    {b.admin_note && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">Nota: {b.admin_note}</div>}
                                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(b.created_at).toLocaleString('es-MX')}</div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    {b.link_url && (
                                        <a href={b.link_url} target="_blank" rel="noopener noreferrer">
                                            <Button size="sm" variant="ghost"><Eye className="h-4 w-4" /></Button>
                                        </a>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => openReview(b)} data-testid={`review-${b.id}`}>
                                        Revisar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Review dialog */}
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Revisar banner</DialogTitle></DialogHeader>
                    {selected && (
                        <div className="space-y-4">
                            {selected.image_url && (
                                <img
                                    src={selected.image_url.startsWith('http') ? selected.image_url : `/api/media/${selected.image_url.replace(/^\/api\/media\//, '')}`}
                                    alt={selected.title}
                                    className="w-full h-32 object-cover rounded-xl border"
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                            )}
                            <div className="rounded-xl border p-3 space-y-1 bg-secondary/30 text-sm">
                                <div><span className="font-medium">Título:</span> {selected.title}</div>
                                <div><span className="font-medium">Vendedor:</span> @{selected.seller_nickname}</div>
                                <div><span className="font-medium">Posición:</span> {BANNER_PLACEMENTS[selected.placement] || selected.placement}</div>
                                {selected.link_url && <div><span className="font-medium">Enlace:</span> <a href={selected.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{selected.link_url}</a></div>}
                            </div>
                            <div>
                                <Label>Nota para el vendedor (opcional)</Label>
                                <Input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Ej: Aprobado. Activo en 1 hora." />
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={() => setReviewOpen(false)}>Cancelar</Button>
                        {selected?.status === 'active' ? (
                            <Button variant="outline" className="border-amber-200 text-amber-700" onClick={() => doAction('pause')} disabled={busy}>
                                Pausar
                            </Button>
                        ) : (
                            <>
                                <Button variant="outline" className="border-red-200 text-red-700" onClick={() => doAction('reject')} disabled={busy}>
                                    <XCircle className="h-4 w-4 mr-1" />Rechazar
                                </Button>
                                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction('approve')} disabled={busy}>
                                    <CheckCircle2 className="h-4 w-4 mr-1" />Aprobar
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pricing config dialog */}
            {pricingForm && (
                <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Configurar precios de banners</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div className="font-medium text-sm">Paquetes de slots de banner</div>
                            <div className="space-y-3">
                                {(pricingForm.packages || []).map((p, i) => (
                                    <div key={i} className="grid grid-cols-4 gap-2 items-end">
                                        <div>
                                            <Label className="text-xs">Etiqueta</Label>
                                            <Input value={p.label} onChange={e => {
                                                const packages = [...pricingForm.packages];
                                                packages[i] = { ...p, label: e.target.value };
                                                setPricingForm(f => ({ ...f, packages }));
                                            }} className="h-8" placeholder="Básico" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Slots</Label>
                                            <Input type="number" min="1" value={p.slots} onChange={e => {
                                                const packages = [...pricingForm.packages];
                                                packages[i] = { ...p, slots: Number(e.target.value) };
                                                setPricingForm(f => ({ ...f, packages }));
                                            }} className="h-8" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Días</Label>
                                            <Input type="number" min="1" value={p.days} onChange={e => {
                                                const packages = [...pricingForm.packages];
                                                packages[i] = { ...p, days: Number(e.target.value) };
                                                setPricingForm(f => ({ ...f, packages }));
                                            }} className="h-8" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Precio MXN</Label>
                                            <Input type="number" min="0" value={p.price_mxn} onChange={e => {
                                                const packages = [...pricingForm.packages];
                                                packages[i] = { ...p, price_mxn: Number(e.target.value) };
                                                setPricingForm(f => ({ ...f, packages }));
                                            }} className="h-8" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setPricingForm(f => ({ ...f, packages: [...f.packages, { label: 'Nuevo', slots: 1, days: 30, price_mxn: 299 }] }))}>
                                <PlusCircle className="h-3 w-3 mr-1" />Añadir paquete
                            </Button>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setPricingOpen(false)}>Cancelar</Button>
                            <Button onClick={savePricing} disabled={busy}>Guardar precios</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
